import { WithChildren } from '@/types/common'
import { createContext } from '@/ultils'
import { useCallback, useMemo, useReducer } from 'react'

const initialState: State = {
  displaySidebar: false,
  displayEditSidebar: false,
  displayModal: false,
  sidebarView: {
    type: '',
    id: '',
  },
  modalView: {
    type: '',
    id: '',
  },
}

export interface State {
  displaySidebar: boolean
  displayEditSidebar: boolean
  displayModal: boolean
  sidebarView: SIDEBAR_VIEW
  modalView: MODAL_VIEW
}

export type SIDEBAR_VIEW = {
  type: SIDEBAR_TYPES
  id?: string
}

export type MODAL_VIEW = {
  type: MODAL_TYPES
  id?: string
  data?: any
}

type Action =
  | {
      type: 'OPEN_SIDEBAR'
    }
  | {
      type: 'CLOSE_SIDEBAR'
    }
  | {
      type: 'SET_SIDEBAR_VIEW'
      sidebarView: SIDEBAR_VIEW
    }
  | {
      type: 'OPEN_MODAL'
    }
  | {
      type: 'CLOSE_MODAL'
    }
  | {
      type: 'SET_MODAL_VIEW'
      modalView: MODAL_VIEW
    }

export type SIDEBAR_TYPES = 'SETUP_VIEW' | ''
export type MODAL_TYPES =
  | 'REVIEW_DECODERS_VIEW'
  | 'CONTROLLER_MODAL'
  | 'CONFIGURATION_MODAL'
  | 'MK2_DECODER_MODAL'
  | 'MK2_DECODER_MODAL_V2'
  | ''

interface SetUpContextValues extends State {
  openSidebar: () => void
  closeSidebar: () => void
  toggleSidebar: () => void
  setSidebarView: (sidebarView: SIDEBAR_VIEW) => void
  openModal: () => void
  closeModal: () => void
  setModalView: (modalView: MODAL_VIEW) => void
}

function uiReducer(state: State, action: Action) {
  switch (action.type) {
    case 'OPEN_SIDEBAR': {
      return {
        ...state,
        displaySidebar: true,
      }
    }
    case 'CLOSE_SIDEBAR': {
      return {
        ...state,
        displaySidebar: false,
      }
    }
    case 'SET_SIDEBAR_VIEW': {
      return {
        ...state,
        sidebarView: {
          ...action.sidebarView,
        },
      }
    }

    case 'OPEN_MODAL': {
      return {
        ...state,
        displayModal: true,
      }
    }
    case 'CLOSE_MODAL': {
      return {
        ...state,
        displayModal: false,
      }
    }
    case 'SET_MODAL_VIEW': {
      return {
        ...state,
        modalView: action.modalView,
      }
    }
  }
}

const [Provider, useSetUpUI] = createContext<SetUpContextValues>({
  name: 'adminUI',
})

const SetUpUIProvider = ({ children }: WithChildren) => {
  const [state, dispatch] = useReducer(uiReducer, initialState)

  const openSidebar = useCallback(
    () => dispatch({ type: 'OPEN_SIDEBAR' }),
    [dispatch],
  )

  const closeSidebar = useCallback(
    () => dispatch({ type: 'CLOSE_SIDEBAR' }),
    [dispatch],
  )

  const toggleSidebar = useCallback(
    () =>
      state.displaySidebar
        ? dispatch({ type: 'CLOSE_SIDEBAR' })
        : dispatch({ type: 'OPEN_SIDEBAR' }),
    [dispatch, state.displaySidebar],
  )

  const setSidebarView = useCallback(
    (sidebarView: SIDEBAR_VIEW) =>
      dispatch({ type: 'SET_SIDEBAR_VIEW', sidebarView }),
    [dispatch],
  )

  const openModal = useCallback(
    () => dispatch({ type: 'OPEN_MODAL' }),
    [dispatch],
  )

  const closeModal = useCallback(
    () => dispatch({ type: 'CLOSE_MODAL' }),
    [dispatch],
  )

  const setModalView = useCallback(
    (modalView: MODAL_VIEW) => dispatch({ type: 'SET_MODAL_VIEW', modalView }),
    [dispatch],
  )

  const value = useMemo(
    () => ({
      ...state,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      setSidebarView,
      openModal,
      closeModal,
      setModalView,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state],
  )

  return <Provider value={value}>{children}</Provider>
}

export { SetUpUIProvider, useSetUpUI }
